// src/admin/api/fetchAllPages.js
//
// The admin list pages sort, search and paginate client-side through
// DataTable — the pattern accounts/pages/AccountsPage.jsx and
// connectors/pages/ConnectorsPage.jsx established — which needs the whole
// collection in memory.
//
// Every RBAC/identity list endpoint caps page_size at 100
// (`Query(20, ge=1, le=100)` in rbac_controller.py and user_controller.py —
// asking for more is a 422), so "the whole collection" means walking pages.
// All three admin catalogs are bounded, slow-moving reference data (tens of
// rows), so this is one round trip in practice.

import { api } from "../../api/client";

/** The server-side cap. Requesting more than this is rejected, not clamped. */
export const MAX_PAGE_SIZE = 100;

/**
 * Fetches every page of a paginated `{items, total_pages}` endpoint and
 * returns the combined `items`.
 *
 * Page 1 goes first because that's what reports `total_pages`; the remainder
 * are then requested in parallel rather than in sequence.
 *
 * Errors propagate untouched — each API module wraps them with its own
 * message via toAdminError.
 */
export async function fetchAllPages(path, params = {}) {
  const first = await api.get(path, {
    params: { ...params, page: 1, page_size: MAX_PAGE_SIZE },
  });

  const { items = [], total_pages: totalPages = 1 } = first.data || {};
  const all = [...items];

  if (totalPages > 1) {
    const rest = await Promise.all(
      Array.from({ length: totalPages - 1 }, (_, i) =>
        api.get(path, { params: { ...params, page: i + 2, page_size: MAX_PAGE_SIZE } })
      )
    );
    rest.forEach((res) => all.push(...(res.data?.items || [])));
  }

  return all;
}
